"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PropertyShape = exports.ValidationResult = exports.NodeShape = exports.SHACL_Shape = void 0;
/*
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 */
const models_1 = require("../models");
const Shape_1 = require("./Shape");
const shacl_1 = require("../ontologies/shacl");
const List_1 = require("./List");
const xsd_1 = require("../ontologies/xsd");
const NodeSet_1 = require("../collections/NodeSet");
const rdf_1 = require("../ontologies/rdf");
class SHACL_Shape extends Shape_1.Shape {
}
exports.SHACL_Shape = SHACL_Shape;
SHACL_Shape.targetClass = shacl_1.shacl.Shape;
class NodeShape extends SHACL_Shape {
    addPropertyShape(property) {
        this.set(shacl_1.shacl.property, property.namedNode);
    }
    getPropertyShapes() {
        return PropertyShape.getSetOf(this.getAll(shacl_1.shacl.property));
    }
    get targetNode() {
        return this.getOne(shacl_1.shacl.targetNode);
    }
    set targetNode(value) {
        this.overwrite(shacl_1.shacl.targetNode, value);
    }
    get targetClass() {
        return this.getOne(shacl_1.shacl.targetClass);
    }
    set targetClass(value) {
        this.overwrite(shacl_1.shacl.targetClass, value);
    }
    get in() {
        return this.getOne(shacl_1.shacl.in);
    }
    set in(value) {
        this.overwrite(shacl_1.shacl.in, value);
    }
    get inList() {
        return this.hasProperty(shacl_1.shacl.in) ? List_1.List.getOf(this.getOne(shacl_1.shacl.in)) : null;
    }
    set inList(value) {
        this.overwrite(shacl_1.shacl.in, value.node);
    }
    /**
     * Returns all the classes and properties that are references by this shape
     */
    getOntologyEntities() {
        let entities = new NodeSet_1.NodeSet();
        if (this.targetClass) {
            entities.add(this.targetClass);
        }
        //add ontology entities of all property shapes
        this.getPropertyShapes().forEach((propertyShape) => {
            entities = entities.concat(propertyShape.getOntologyEntities());
        });
        return entities;
    }
    validate(node) {
        if (this.targetClass) {
            if (!(node instanceof models_1.NamedNode && node.has(rdf_1.rdf.type, this.targetClass))) {
                return false;
            }
        }
        let propertyShapes = this.getPropertyShapes();
        if (propertyShapes.size > 0) {
            if (node instanceof models_1.Literal) {
                return false;
            }
            else if (node instanceof models_1.NamedNode) {
                if (!this.getPropertyShapes().every((propertyShape) => {
                    return propertyShape.validate(node);
                })) {
                    return false;
                }
            }
        }
        return true;
    }
    static getShapesOf(node) {
        return this.getLocalInstances().filter((shape) => {
            return shape.validate(node);
        });
    }
}
exports.NodeShape = NodeShape;
NodeShape.targetClass = shacl_1.shacl.NodeShape;
class ValidationResult {
}
exports.ValidationResult = ValidationResult;
class PropertyShape extends SHACL_Shape {
    get class() {
        return this.getOne(shacl_1.shacl.class);
    }
    set class(value) {
        this.overwrite(shacl_1.shacl.class, value);
    }
    get nodeShape() {
        return this.hasProperty(shacl_1.shacl.node) ? NodeShape.getOf(this.getOne(shacl_1.shacl.node)) : null;
    }
    set nodeShape(value) {
        this.overwrite(shacl_1.shacl.node, value.node);
    }
    get nodeKind() {
        return this.getOne(shacl_1.shacl.nodeKind);
    }
    set nodeKind(value) {
        this.overwrite(shacl_1.shacl.nodeKind, value);
    }
    get datatype() {
        return this.getOne(shacl_1.shacl.datatype);
    }
    set datatype(value) {
        this.overwrite(shacl_1.shacl.datatype, value);
    }
    get maxCount() {
        return parseInt(this.getValue(shacl_1.shacl.maxCount));
    }
    set maxCount(value) {
        this.overwrite(shacl_1.shacl.maxCount, new models_1.Literal(value.toString(), xsd_1.xsd.integer));
    }
    get minCount() {
        return parseInt(this.getValue(shacl_1.shacl.minCount));
    }
    set minCount(value) {
        this.overwrite(shacl_1.shacl.minCount, new models_1.Literal(value.toString(), xsd_1.xsd.integer));
    }
    get name() {
        return this.getValue(shacl_1.shacl.name);
    }
    // Setter overloading - would be nice to have one for String and another for Literal:
    // https://github.com/microsoft/TypeScript/issues/2521
    set name(value) {
        this.overwrite(shacl_1.shacl.name, new models_1.Literal(value));
    }
    get optional() {
        return this.getValue(shacl_1.shacl.optional);
    }
    set optional(value) {
        this.overwrite(shacl_1.shacl.optional, new models_1.Literal(value, xsd_1.xsd.boolean));
    }
    get path() {
        return this.getOne(shacl_1.shacl.path);
    }
    set path(value) {
        this.overwrite(shacl_1.shacl.path, value);
    }
    /**
     * Returns all the classes and properties that are references by this shape
     */
    getOntologyEntities() {
        //start with values of those properties that have a NamedNode as value
        let entities = new NodeSet_1.NodeSet([this.class, this.path, this.datatype].filter((value) => value && true));
        //this caused loops!
        // if (this.nodeShape) {
        //if a node shape is defined, also add all the entities of that node shape
        // entities = entities.concat(this.nodeShape.getOntologyEntities());
        // }
        return entities;
    }
    validate(node) {
        //TODO: make property nodes support property paths beyond a single property
        let property = this.path;
        let values = node instanceof models_1.NamedNode ? node.getAll(property) : null;
        if (this.class) {
            if (!values.every((value) => value instanceof models_1.NamedNode && value.has(rdf_1.rdf.type, this.class))) {
                return false;
            }
        }
        if (this.datatype) {
            if (!values.every((value) => value instanceof models_1.Literal && value.datatype === this.datatype)) {
                return false;
            }
        }
        if (this.nodeShape) {
            //every value should be a valid instance of this nodeShape
            let nodeShape = this.nodeShape;
            if (!values.every((value) => nodeShape.validate(value))) {
                return false;
            }
        }
        if (this.minCount) {
            if (values.size < this.minCount) {
                return false;
            }
        }
        if (this.maxCount) {
            if (values.size > this.maxCount) {
                return false;
            }
        }
        return true;
    }
    resolveFor(node) {
        //TODO: support more complex property paths
        return node.getAll(this.path);
    }
}
exports.PropertyShape = PropertyShape;
PropertyShape.targetClass = shacl_1.shacl.PropertyShape;
//# sourceMappingURL=SHACL.js.map